import { CommonModule } from '@angular/common';
import {
  AfterContentInit,
  Component,
  ContentChildren,
  EventEmitter,
  HostListener,
  ElementRef,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { SkeletonComponent } from '../skeleton/skeleton.component';
import { InfoIconComponent } from '../info-icon/info-icon.component';
import {
  DataTableCellContext,
  DataTableCellDirective,
} from './data-table-cell.directive';

export interface DataTableColumn {
  key: string;
  label: string;
  width?: string;
  sortable?: boolean;
  /** Wrap cell content across lines instead of the default single-line ellipsis. */
  wrap?: boolean;
  /** Horizontal alignment of the header and its cells. Numeric columns should use `end`. */
  align?: 'start' | 'center' | 'end';
  /** Definition of the column, shown in a tooltip from an info button beside its label. */
  tooltip?: string;
}

/** A row as rendered: in a tree table the children of an expanded row follow it. */
export interface DataTableDisplayRow {
  row: unknown;
  level: number;
  parent: unknown;
  hasChildren: boolean;
  expanded: boolean;
  lastChild: boolean;
  id: string;
  /** Ids of the rendered child rows, for the expand button's `aria-controls`. */
  childIds: string | null;
  toggleLabel: string;
}

export type DataTableSortDirection = 'asc' | 'desc';

export interface DataTableSortState {
  key: string;
  direction: DataTableSortDirection;
}

/**
 * Themed, reusable results table: bordered card, header band, hoverable rows,
 * and a footer with built-in pagination (always visible, even for a single
 * page — the nav controls simply disable themselves).
 * Custom cell markup for a column is supplied via
 * `<ng-template appDataTableCell="columnKey" let-row>`. Sortable columns emit
 * `sortChange`; the consumer owns actually reordering/refetching `rows`.
 */
@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    SkeletonComponent,
    InfoIconComponent,
  ],
  templateUrl: './data-table.component.html',
  styleUrl: './data-table.component.scss',
})
export class DataTableComponent implements AfterContentInit, OnChanges {
  @ViewChild('scrollRegion') private scrollRegion?: ElementRef<HTMLElement>;
  @Input() columns: DataTableColumn[] = [];
  @Input() rows: unknown[] = [];
  @Input() loading = false;
  @Input() showFooter = true;
  @Input() ariaLabel = '';
  @Input() compact = false;
  @Input() rowHeaderKey?: string;
  @Input() rowClickable = false;
  @Input() resizableColumns = false;
  /** Marks the row the page is currently showing (highlighted and exposed as `aria-current`). */
  @Input() isRowSelected: (row: unknown) => boolean = () => false;
  /** Shows only the previous/next buttons instead of First, Last and numbered pages. */
  @Input() compactPagination = false;
  @Input() trackByFn?: (index: number, row: unknown) => unknown;
  @Input() testId = 'app-data-table';
  /** Stamped on every data row so consumers can assert row counts in tests. */
  @Input() rowTestId?: string;
  /**
   * Turns the table into a two-level tree: the rows returned for a row are listed under it,
   * and its first cell gets an expand/collapse button. Rows start collapsed. Top-level rows are
   * group headings: only child rows are clickable and selectable. Pagination counts top-level
   * rows.
   */
  @Input() childRows?: (row: unknown) => readonly unknown[] | null | undefined;
  /** Names a top-level row in its expand/collapse button's accessible name. */
  @Input() rowLabel: (row: unknown) => string = () => 'row';

  @Input() set pageSize(size: number) {
    this._pageSize = size;
    this.skeletonRows = Array.from(
      { length: Math.max(0, size) },
      (_, index) => index,
    );
  }
  get pageSize(): number {
    return this._pageSize;
  }
  @Input() pageSizeOptions: readonly number[] = [10, 20, 50];
  @Input() currentPage = 1;
  @Input() totalItems = 0;

  @Output() readonly rowClick = new EventEmitter<unknown>();
  @Output() readonly pageChange = new EventEmitter<number>();
  @Output() readonly pageSizeChange = new EventEmitter<number>();
  @Output() readonly sortChange = new EventEmitter<DataTableSortState>();

  sortState: DataTableSortState | null = null;
  skeletonRows: number[] = [];
  displayRows: DataTableDisplayRow[] = [];
  // Keyed by row object, so a refreshed page of new rows starts expanded again.
  private expandedRows = new WeakSet<object>();
  private _pageSize = 0;
  private columnWidths: number[] | null = null;
  private activeResize: {
    pointerId: number;
    index: number;
    startX: number;
    widths: number[];
    minimums: number[];
  } | null = null;

  @ContentChildren(DataTableCellDirective)
  private cellTemplateDirectives?: QueryList<DataTableCellDirective>;

  private cellTemplatesByColumn = new Map<
    string,
    TemplateRef<DataTableCellContext>
  >();

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['rows'] ||
      changes['childRows'] ||
      changes['rowLabel'] ||
      changes['testId']
    ) {
      this.buildDisplayRows();
    }

    const pageChange = changes['currentPage'];
    const sizeChange = changes['pageSize'];
    if (
      (pageChange && !pageChange.firstChange) ||
      (sizeChange && !sizeChange.firstChange)
    ) {
      const region = this.scrollRegion?.nativeElement;
      if (region) {
        region.scrollTop = 0;
      }
    }
  }

  ngAfterContentInit(): void {
    this.cellTemplateDirectives?.forEach((directive) => {
      this.cellTemplatesByColumn.set(
        directive.columnKey,
        directive.templateRef,
      );
    });
  }

  getCellTemplate(key: string): TemplateRef<DataTableCellContext> | null {
    return this.cellTemplatesByColumn.get(key) ?? null;
  }

  get columnTemplate(): string {
    return this.columnWidths
      ? this.columnWidths.map((width) => `${width}%`).join(' ')
      : this.columns.map((column) => column.width ?? '1fr').join(' ');
  }

  @HostListener('window:resize')
  onViewportResize(): void {
    this.columnWidths = null;
  }

  onResizeStart(event: PointerEvent, index: number): void {
    if (event.button !== 0) {
      return;
    }

    const handle = event.currentTarget as HTMLElement;
    const measurement = this.measureColumns(handle);
    if (!measurement || index >= measurement.widths.length - 1) {
      return;
    }

    event.preventDefault();
    handle.setPointerCapture(event.pointerId);
    this.activeResize = {
      pointerId: event.pointerId,
      index,
      startX: event.clientX,
      ...measurement,
    };
  }

  onResizeMove(event: PointerEvent): void {
    const resize = this.activeResize;
    if (resize?.pointerId !== event.pointerId) {
      return;
    }

    this.resizeColumns(
      resize.widths,
      resize.minimums,
      resize.index,
      event.clientX - resize.startX,
    );
  }

  onResizeEnd(event: PointerEvent): void {
    if (this.activeResize?.pointerId === event.pointerId) {
      this.activeResize = null;
      const handle = event.currentTarget as HTMLElement;
      if (handle.hasPointerCapture(event.pointerId)) {
        handle.releasePointerCapture(event.pointerId);
      }
    }
  }

  onResizeKeyDown(event: KeyboardEvent, index: number): void {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }

    const measurement = this.measureColumns(event.currentTarget as HTMLElement);
    if (!measurement) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.resizeColumns(
      measurement.widths,
      measurement.minimums,
      index,
      event.key === 'ArrowRight' ? 16 : -16,
    );
  }

  private measureColumns(
    handle: HTMLElement,
  ): { widths: number[]; minimums: number[] } | null {
    const row = handle.closest('tr');
    if (!row) {
      return null;
    }

    const headers = Array.from(row.querySelectorAll('th'));
    const widths = headers.map((cell) => cell.getBoundingClientRect().width);
    const minimums = headers.map((cell, index) =>
      Math.min(
        widths[index],
        Math.max(
          96,
          (cell.querySelector('.app-data-table__sort-label')?.scrollWidth ??
            0) + 56,
        ),
      ),
    );
    return { widths, minimums };
  }

  private resizeColumns(
    widths: number[],
    minimums: number[],
    index: number,
    change: number,
  ): void {
    const left = widths[index];
    const right = widths[index + 1];
    if (left === undefined || right === undefined) {
      return;
    }

    const pairWidth = left + right;
    const nextLeft = Math.max(
      minimums[index],
      Math.min(pairWidth - minimums[index + 1], left + change),
    );
    const nextWidths = [...widths];
    nextWidths[index] = nextLeft;
    nextWidths[index + 1] = pairWidth - nextLeft;
    const totalWidth = nextWidths.reduce((total, width) => total + width, 0);
    this.columnWidths = nextWidths.map((width) => (width / totalWidth) * 100);
  }

  trackByRow = (index: number, row: unknown): unknown =>
    this.trackByFn ? this.trackByFn(index, row) : index;

  trackByDisplayRow = (index: number, item: DataTableDisplayRow): unknown =>
    this.trackByRow(index, item.row);

  get isTree(): boolean {
    return !!this.childRows;
  }

  /** In a tree table the top-level rows only group their children. */
  isGroupRow(item: DataTableDisplayRow): boolean {
    return this.isTree && item.level === 0;
  }

  toggleRow(item: DataTableDisplayRow, event: Event): void {
    event.stopPropagation();
    const row = item.row as object;
    if (this.expandedRows.has(row)) {
      this.expandedRows.delete(row);
    } else {
      this.expandedRows.add(row);
    }
    this.buildDisplayRows();
  }

  private buildDisplayRows(): void {
    const rows = this.rows ?? [];
    this.displayRows = rows.flatMap((row, index) => {
      const id = `${this.testId}-row-${index}`;
      const children = this.childRows?.(row) ?? [];
      const expanded =
        children.length > 0 && this.expandedRows.has(row as object);
      const childItems: DataTableDisplayRow[] = expanded
        ? children.map((child, childIndex) => ({
            row: child,
            level: 1,
            parent: row,
            hasChildren: false,
            expanded: false,
            lastChild: childIndex === children.length - 1,
            id: `${id}-${childIndex}`,
            childIds: null,
            toggleLabel: '',
          }))
        : [];

      return [
        {
          row,
          level: 0,
          parent: null,
          hasChildren: children.length > 0,
          expanded,
          lastChild: false,
          id,
          childIds: expanded
            ? childItems.map((child) => child.id).join(' ')
            : null,
          toggleLabel: this.isTree
            ? `${expanded ? 'Collapse' : 'Expand'} ${this.rowLabel(row)}`
            : '',
        },
        ...childItems,
      ];
    });
  }

  trackByPage = (_index: number, page: number): number => page;

  get totalPages(): number {
    if (!this.showFooter || this.pageSize <= 0) {
      return 1;
    }

    return Math.max(1, Math.ceil(this.totalItems / this.pageSize));
  }

  /** 1-based position of the first row on the current page (0 when there are no rows). */
  get rangeStart(): number {
    if (this.totalItems <= 0) {
      return 0;
    }

    return (this.currentPage - 1) * this.pageSize + 1;
  }

  /** 1-based position of the last row on the current page. */
  get rangeEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  get pageLinks(): number[] {
    if (!this.showFooter) {
      return [];
    }

    const start = Math.max(
      1,
      Math.min(this.currentPage - 2, this.totalPages - 4),
    );
    return Array.from(
      { length: Math.min(5, this.totalPages) },
      (_, index) => start + index,
    );
  }

  onRowClick(item: DataTableDisplayRow): void {
    if (this.rowClickable && !this.isGroupRow(item)) {
      this.rowClick.emit(item.row);
    }
  }

  onPageClick(page: number): void {
    if (
      !this.showFooter ||
      this.loading ||
      page < 1 ||
      page > this.totalPages ||
      page === this.currentPage
    ) {
      return;
    }

    this.pageChange.emit(page);
  }

  onFirst(): void {
    this.onPageClick(1);
  }

  onLast(): void {
    this.onPageClick(this.totalPages);
  }

  onNext(): void {
    this.onPageClick(this.currentPage + 1);
  }

  onPrevious(): void {
    this.onPageClick(this.currentPage - 1);
  }

  onPageSizeChange(event: MatSelectChange): void {
    if (!this.showFooter || this.loading) {
      return;
    }

    const size = event.value as number;
    if (this.pageSizeOptions.includes(size) && size !== this.pageSize) {
      this.pageSizeChange.emit(size);
    }
  }

  getAriaSort(column: DataTableColumn): 'ascending' | 'descending' | 'none' {
    if (!column.sortable || this.sortState?.key !== column.key) {
      return 'none';
    }

    return this.sortState.direction === 'asc' ? 'ascending' : 'descending';
  }

  getSortIcon(column: DataTableColumn): string {
    if (this.sortState?.key !== column.key) {
      return 'unfold_more';
    }

    return this.sortState.direction === 'asc'
      ? 'arrow_upward'
      : 'arrow_downward';
  }

  onHeaderClick(column: DataTableColumn): void {
    if (!column.sortable) {
      return;
    }

    const direction: DataTableSortDirection =
      this.sortState?.key === column.key && this.sortState.direction === 'asc'
        ? 'desc'
        : 'asc';

    this.sortState = { key: column.key, direction };
    this.sortChange.emit({ ...this.sortState });
  }
}
